import { useState, useEffect, useRef, useCallback } from 'react';
import { ActivityCategory } from '../types/user';

export interface ActivitySuggestion {
  timeAway: number;
  suggestedCategory: ActivityCategory;
  suggestedText: string;
  placeName?: string;
}

const URL_PATTERNS: { pattern: RegExp; category: ActivityCategory; text: string }[] = [
  { pattern: /youtube\.com|netflix\.com|twitch\.tv|hulu|disneyplus|prime\.video/i, category: 'social',  text: 'Watching videos' },
  { pattern: /amazon\.|ebay\.|shopify|zalando|aliexpress|etsy\./i,                  category: 'other',   text: 'Shopping online' },
  { pattern: /github\.com|stackoverflow\.com|dev\.to|mdn\.|npmjs/i,                category: 'work',    text: 'Coding' },
  { pattern: /twitter\.com|x\.com|instagram\.com|facebook\.com|tiktok\.com/i,      category: 'social',  text: 'Social media' },
  { pattern: /spotify\.com|soundcloud\.com|music\.apple|deezer/i,                  category: 'home',    text: 'Listening to music' },
  { pattern: /linkedin\.com/i,                                                       category: 'work',    text: 'Networking' },
  { pattern: /reddit\.com/i,                                                         category: 'home',    text: 'Browsing Reddit' },
  { pattern: /maps\.google|google\.com\/maps|maps\.apple|waze/i,                   category: 'transit', text: 'Planning a route' },
  { pattern: /duolingo|coursera|udemy|khanacademy|skillshare/i,                      category: 'study',   text: 'Learning online' },
  { pattern: /notion\.|confluence\.|docs\.google|office\.com/i,                     category: 'work',    text: 'Working on docs' },
  { pattern: /chess\.com|lichess|steam\.|epicgames/i,                               category: 'gaming',  text: 'Gaming' },
];

export function detectURLActivity(url: string): { category: ActivityCategory; text: string } | null {
  for (const p of URL_PATTERNS) {
    if (p.pattern.test(url)) return { category: p.category, text: p.text };
  }
  return null;
}

function getTimeSuggestion(mins: number): { category: ActivityCategory; text: string } {
  const h = new Date().getHours();
  if (mins < 5)                               return { category: 'other',   text: 'Quick break' };
  if (h >= 5  && h < 9  && mins > 20)        return { category: 'fitness', text: 'Morning routine' };
  if (h >= 12 && h < 14)                     return { category: 'food',    text: 'Lunch break' };
  if (h >= 18 && h < 22 && mins > 60)        return { category: 'social',  text: 'Evening out' };
  if ((h >= 22 || h < 5) && mins > 240)      return { category: 'home',    text: 'Sleeping' };
  if (mins > 30 && mins < 120)               return { category: 'food',    text: 'Having a meal' };
  if (mins > 120)                            return { category: 'social',  text: 'Out and about' };
  return { category: 'other', text: 'Something offline' };
}

// Simple geocode cache to avoid hammering Nominatim
const geocodeCache = new Map<string, string | undefined>();

function geocodeCacheKey(lat: number, lng: number): string {
  // Round to ~100m grid for cache hits
  return `${(lat * 1000 | 0)},${(lng * 1000 | 0)}`;
}

async function reverseGeocode(lat: number, lng: number): Promise<string | undefined> {
  const cacheKey = geocodeCacheKey(lat, lng);
  if (geocodeCache.has(cacheKey)) return geocodeCache.get(cacheKey);

  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=en`,
      { headers: { 'User-Agent': 'Joystance/1.0' } }
    );
    const d = await res.json();
    const name = d.address?.amenity || d.address?.leisure || d.address?.shop
      || d.address?.road || d.address?.suburb || d.address?.city;
    geocodeCache.set(cacheKey, name);
    return name;
  } catch {
    return undefined;
  }
}

export function useActivityTracker(lat: number | null, lng: number | null) {
  const [suggestion, setSuggestion] = useState<ActivitySuggestion | null>(null);
  const hiddenAt = useRef<number | null>(null);
  // Capture position at hide time for accurate geocoding
  const hiddenPos = useRef<{ lat: number | null; lng: number | null }>({ lat: null, lng: null });
  const posRef = useRef({ lat, lng });

  useEffect(() => { posRef.current = { lat, lng }; }, [lat, lng]);

  useEffect(() => {
    const onChange = async () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt.current = Date.now();
        // Snapshot position when user leaves
        hiddenPos.current = { ...posRef.current };
        return;
      }
      if (document.visibilityState === 'visible' && hiddenAt.current) {
        const timeAway = Date.now() - hiddenAt.current;
        hiddenAt.current = null;
        if (timeAway < 3 * 60_000) return;

        const mins = timeAway / 60_000;
        const { category, text } = getTimeSuggestion(mins);

        let placeName: string | undefined;
        // Use the position from when they left, not current
        const { lat: hidLat, lng: hidLng } = hiddenPos.current;
        if (hidLat && hidLng) placeName = await reverseGeocode(hidLat, hidLng);

        setSuggestion({ timeAway, suggestedCategory: category, suggestedText: text, placeName });
      }
    };
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);

  const dismiss = useCallback(() => setSuggestion(null), []);
  return { suggestion, dismiss };
}
